import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getWorkout, updateWorkout } from "api-client";
import { EditableInput } from "ui";

import { SectionEditSkeleton } from "src/components/SectionEditSkeleton";
import { SectionEditContent } from "src/sections/SectionEditContent";

interface SectionEditLoaderProps {
  workoutId: string;
  disabled?: boolean;
}

export function SectionEditLoader(props: SectionEditLoaderProps) {
  const queryClient = useQueryClient();
  const workoutQuery = useQuery({
    queryKey: ["section", props.workoutId],
    queryFn: async () => {
      const res = await getWorkout({ path: { workoutId: props.workoutId } });
      return res.data.data;
    },
  });
  const updateNameMutation = useMutation({
    mutationFn: (name: string) => {
      return updateWorkout({
        path: { workoutId: props.workoutId },
        body: { name, description: workoutQuery.data?.description ?? "" },
      });
    },
    onMutate: (name) => {
      queryClient.setQueryData(
        ["section", props.workoutId],
        (old: typeof workoutQuery.data) => {
          return old ? { ...old, name } : old;
        },
      );
    },
  });

  if (workoutQuery.isLoading) return <SectionEditSkeleton />;

  if (!workoutQuery.data) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b px-4 pr-12 pt-2 pb-2">
        <EditableInput
          value={workoutQuery.data.name}
          onSubmit={(name) => updateNameMutation.mutate(name)}
          className="text-lg font-semibold"
          disabled={props.disabled}
        />
      </div>
      <SectionEditContent
        key={props.workoutId}
        workout={workoutQuery.data}
        disabled={props.disabled}
      />
    </div>
  );
}
